import React from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from 'redux';
import { AppState } from 'app/store';
import * as SessionStorageSelectors from 'app/models/sessionStorage/selectors';
import * as SessionStorageActions from 'app/models/sessionStorage/actions';
import * as LocalStorageSelectors from 'app/models/localStorage/selectors';
import * as LocalStorageActions from 'app/models/localStorage/actions';

export interface IStorageParser<ValueType> {
  serialize: (value: ValueType) => any;
  deserialize: (storedValue: unknown) => ValueType;
  isEqual?: (a: ValueType, b: ValueType) => boolean;
}

// TODO: This is very similar to createParamContext(). Maybe they should share code?
export default function createStorageConsumer<ValueType>(
  storageType: 'localStorage' | 'sessionStorage',
  storageKey: string,
  storageParser: IStorageParser<ValueType>,
) {
  const isEqual = storageParser.isEqual || ((a: any, b: any): boolean => a === b);

  const storageActions = (
    storageType === 'localStorage' ? LocalStorageActions : SessionStorageActions
  );
  const storageSelectors = (
    storageType === 'localStorage' ? LocalStorageSelectors : SessionStorageSelectors
  );

  type ChildrenType = (args: { value: ValueType, setValue: (value: ValueType) => void }) => React.ReactNode;

  interface IRenderProps {
    children: ChildrenType;
    dispatch: AppDispatch;
    value: ValueType;
  }

  class Render extends React.Component<IRenderProps> {
    public shouldComponentUpdate(nextProps: IRenderProps) {
      return (
        nextProps.children !== this.props.children ||
        nextProps.dispatch !== this.props.dispatch ||
        !isEqual(nextProps.value, this.props.value)
      );
    }

    public render() {
      const { children, value } = this.props;
      const { setValue } = this;

      return children({ value, setValue });
    }

    private setValue = (value: ValueType) => {
      const { dispatch } = this.props;

      const serializedValue = storageParser.serialize(value);

      // Special case: `serialize()` returning `null` means to remove the value
      if (serializedValue === null) {
        dispatch(storageActions.removeItem(storageKey));
      } else {
        dispatch(storageActions.setItem(storageKey, serializedValue));
      }
    };
  }

  const mapStateToProps = (state: AppState): { value: ValueType } => ({
    value: storageParser.deserialize(
      // Note that getItem does not return the raw strings, but first parses it with JSON.parse
      storageSelectors.getItem(state, storageKey),
    ),
  });

  // Work around issue with TypeScript + React typings + React-Redux typings
  return connect(
    mapStateToProps as (state: AppState) => { value: unknown },
  )(
    Render as
    React.ComponentType<{
      children: ChildrenType;
      dispatch: AppDispatch;
      value: unknown;
    }>,
  ) as unknown as React.ComponentType<{ children: ChildrenType }>;
}
